import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    Box,
    Typography,
    Button,
    Card,
    LinearProgress,
    Alert,
    IconButton,
} from '@mui/material';
import {
    CloudUpload,
    Image as ImageIcon,
    Delete,
    Info,
} from '@mui/icons-material';
import { useInput, useTranslate, useNotify } from 'react-admin';
import { API_CONFIG } from '../../config/api';
import { tokenManager } from '../../utils/tokenManager';

interface BannerImageUploadProps {
    source: string;
    label?: string;
    accept?: Record<string, string[]>;
    maxSize?: number;
}

const BannerImageUpload: React.FC<BannerImageUploadProps> = ({
    source,
    label,
    accept = { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    maxSize = 5242880, // 5MB
}) => {
    const { field } = useInput({ source });
    const translate = useTranslate();
    const notify = useNotify();
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Image resizing function (same as messages)
    const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');

                    // Target aspect ratio is 2:1 (width:height) for banners
                    const targetAspectRatio = 2;
                    const targetWidth = maxWidth;
                    const targetHeight = maxHeight;

                    // Calculate crop dimensions to maintain 2:1 aspect ratio
                    let srcX = 0;
                    let srcY = 0;
                    let srcWidth = img.width;
                    let srcHeight = img.height;

                    const srcAspectRatio = img.width / img.height;

                    if (srcAspectRatio > targetAspectRatio) {
                        // Image is wider than target ratio, crop width
                        srcWidth = img.height * targetAspectRatio;
                        srcX = (img.width - srcWidth) / 2; // Center crop
                    } else if (srcAspectRatio < targetAspectRatio) {
                        // Image is taller than target ratio, crop height
                        srcHeight = img.width / targetAspectRatio;
                        srcY = (img.height - srcHeight) / 2; // Center crop
                    }

                    canvas.width = targetWidth;
                    canvas.height = targetHeight;

                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('Failed to get canvas context'));
                        return;
                    }

                    // Use better image smoothing
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';

                    // Draw the cropped and resized image
                    ctx.drawImage(
                        img,
                        srcX, srcY, srcWidth, srcHeight, // Source (crop)
                        0, 0, targetWidth, targetHeight   // Destination (resize)
                    );

                    // Convert to blob with quality setting
                    canvas.toBlob((blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('Failed to convert canvas to blob'));
                        }
                    }, file.type === 'image/png' ? 'image/png' : 'image/jpeg', 0.85);
                };
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = e.target?.result as string;
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    };

    const onDrop = useCallback(async (acceptedFiles: File[], rejectedFiles: any[]) => {
        setError(null);

        if (rejectedFiles.length > 0) {
            const rejection = rejectedFiles[0];
            if (rejection.errors.some((e: any) => e.code === 'file-too-large')) {
                setError(translate('resources.banners.upload.fileTooLarge', { size: '5MB' }));
            } else if (rejection.errors.some((e: any) => e.code === 'file-invalid-type')) {
                setError(translate('resources.banners.upload.invalidFileType'));
            } else {
                setError(translate('resources.banners.upload.uploadError'));
            }
            return;
        }

        if (acceptedFiles.length === 0) return;

        const file = acceptedFiles[0];
        setUploading(true);

        try {
            // Resize image to 800x400 for banner images (2:1 aspect ratio)
            const resizedBlob = await resizeImage(file, 800, 400);

            // Create FormData for upload
            const formData = new FormData();
            const resizedFile = new File([resizedBlob], file.name, { type: file.type });
            formData.append('file', resizedFile);
            formData.append('type', 'banner');

            // Get auth token
            const token = await tokenManager.getAccessToken();
            if (!token) {
                throw new Error('No authentication token');
            }

            // Upload to backend API (using correct port)
            const response = await fetch(`${API_CONFIG.baseURL}/api/backend/promotion-banners/upload-image`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Upload failed');
            }

            const result = await response.json();

            if (result.data?.imageUrl || result.imageUrl || result.url) {
                const imageUrl = result.data?.imageUrl || result.imageUrl || result.url;
                field.onChange(imageUrl);
                notify('resources.banners.upload.imageUploaded', { type: 'success' });
            } else {
                throw new Error('No image URL returned');
            }
        } catch (err) {
            console.error('Upload error:', err);
            setError(translate('resources.banners.upload.uploadError'));
            notify('resources.banners.upload.uploadError', { type: 'error' });
        } finally {
            setUploading(false);
        }
    }, [field, translate, notify]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept,
        maxSize,
        multiple: false,
    });

    const handleRemove = () => {
        field.onChange(null);
    };

    const currentImageUrl = field.value;

    return (
        <Box sx={{ mb: 2 }}>
            {label && (
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {label}
                </Typography>
            )}

            {currentImageUrl ? (
                <Card sx={{ p: 2, position: 'relative' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            component="img"
                            src={currentImageUrl}
                            alt="Banner preview"
                            sx={{
                                width: 120,
                                height: 60,
                                objectFit: 'cover',
                                borderRadius: 1,
                                border: '1px solid #E0E0E0',
                            }}
                        />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.primary">
                                {translate('resources.banners.upload.imageUploaded')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {translate('resources.banners.upload.aspectRatio')}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleRemove}
                            color="error"
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 1)',
                                },
                            }}
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    </Box>
                </Card>
            ) : (
                <Card
                    {...getRootProps()}
                    sx={{
                        p: { xs: 2, sm: 3 },
                        border: '2px dashed',
                        borderColor: isDragActive ? '#00C853' : '#E0E0E0',
                        backgroundColor: isDragActive ? '#F1F8E9' : '#FAFAFA',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            borderColor: '#00C853',
                            backgroundColor: '#F1F8E9',
                        },
                    }}
                >
                    <input {...getInputProps()} />
                    <Box sx={{ textAlign: 'center' }}>
                        <CloudUpload
                            sx={{
                                fontSize: { xs: 36, sm: 48 },
                                color: isDragActive ? '#00C853' : '#BDBDBD',
                                mb: 1,
                            }}
                        />
                        <Typography variant="h6" sx={{ mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {isDragActive
                                ? translate('resources.banners.upload.dropHere')
                                : translate('resources.banners.upload.dragAndDrop')
                            }
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {translate('resources.banners.upload.supportedFormats')}
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<ImageIcon />}
                            sx={{
                                borderColor: '#00C853',
                                color: '#00C853',
                                '&:hover': {
                                    borderColor: '#00A843',
                                    backgroundColor: 'rgba(0, 200, 83, 0.08)',
                                },
                            }}
                        >
                            {translate('resources.banners.upload.selectFile')}
                        </Button>
                    </Box>
                </Card>
            )}

            {uploading && (
                <Box sx={{ mt: 2 }}>
                    <LinearProgress
                        sx={{
                            backgroundColor: '#E8F5E9',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: '#00C853',
                            },
                        }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {translate('resources.banners.upload.uploading')}
                    </Typography>
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                <Info sx={{ fontSize: 16, color: '#666' }} />
                <Typography variant="caption" color="text.secondary">
                    {translate('resources.banners.upload.requirements')}
                </Typography>
            </Box>
        </Box>
    );
};

export default BannerImageUpload;